import { useEffect, useRef, useState } from "react";
import { updateProject } from "../lib/api/projects";
import type { ProjectPatch } from "../types/project";

type SaveStatus = "idle" | "saving" | "saved" | "error";

interface Options {
  /** Debounce delay in ms. 1s matches "paused-typing" perception. */
  delayMs?: number;
  /** Skip saving (e.g., when project id isn't loaded yet). */
  disabled?: boolean;
}

/**
 * Debounced autosave (A-05).
 * Fires `updateProject(id, patch)` after `delayMs` of no changes.
 * The `patch` is memoized via JSON.stringify of its sorted keys — any
 * shape change in the snapshot triggers a new save.
 */
export function useProjectAutosave(
  projectId: string | null,
  patch: ProjectPatch,
  options: Options = {},
): { status: SaveStatus; lastSavedAt: number | null } {
  const { delayMs = 1000, disabled = false } = options;
  const [status, setStatus] = useState<SaveStatus>("idle");
  const [lastSavedAt, setLastSavedAt] = useState<number | null>(null);

  // Stringify for stable dependency (parts/sheets are arrays that
  // recreate on every render).
  const patchKey = JSON.stringify(patch);

  // Track whether the first run has happened, to skip the initial
  // render's "save" (the component just mounted — no user change yet).
  const initialRef = useRef(true);

  useEffect(() => {
    if (disabled || !projectId) return;
    if (initialRef.current) {
      initialRef.current = false;
      return;
    }

    setStatus("saving");
    const handle = window.setTimeout(async () => {
      const result = await updateProject(projectId, patch);
      if (result.ok) {
        setStatus("saved");
        setLastSavedAt(Date.now());
      } else {
        setStatus("error");
      }
    }, delayMs);

    return () => window.clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, patchKey, delayMs, disabled]);

  return { status, lastSavedAt };
}
